/**
 * The two figures a Nado withdrawal screen has to agree on across platforms: the
 * sequencer fee, and the largest amount the user may type once it is accounted
 * for. A discrepancy here is money.
 */

import { formatUnits } from 'viem'

import { safeBigInt } from '../../src/utils/safeBigInt'
import { x18ToRawBaseUnit, x18ToTokenUnits } from './encoding'
import type { NadoFeeRates } from './gateway'
import type { NadoWithdrawalSourceOption } from './withdrawalTypes'

export interface NadoSequencerFee {
  /** As the gateway reports it: 18 decimals, whatever the token's own. */
  x18: bigint
  /** The token's own units, for the fee the withdrawal flow deducts. */
  tokenAmount: number
}

/** Missing or unparseable reads as 0 — NaN would poison the quote. */
export function getSequencerFee(
  feeRates: NadoFeeRates | undefined,
  option: NadoWithdrawalSourceOption | undefined,
): NadoSequencerFee {
  if (!option) {
    return { x18: 0n, tokenAmount: 0 }
  }
  const x18 = safeBigInt(
    feeRates?.withdraw_sequencer_fees?.[option.productId],
    0n,
  )
  return { x18, tokenAmount: Number(x18ToTokenUnits(x18, option.decimals)) }
}

/**
 * Largest amount the user may type. The fee comes off the entry later, so it
 * goes back on here or Max undershoots by exactly one fee.
 *
 * - Each figure floors before summing; flooring the sum can round up a base
 *   unit past `max_withdrawable`.
 * - Zero, or unknown decimals, gives no number rather than advertising a
 *   maximum the account cannot withdraw.
 */
export function getMaxEnterableAmount({
  maxWithdrawableX18,
  sequencerFee,
  decimals,
}: {
  maxWithdrawableX18: string | undefined
  sequencerFee: bigint
  decimals: number | undefined
}): string {
  const max = safeBigInt(maxWithdrawableX18, 0n)
  if (max <= 0n || decimals === undefined) {
    return '0'
  }
  return formatUnits(
    x18ToRawBaseUnit(max, decimals) + x18ToRawBaseUnit(sequencerFee, decimals),
    decimals,
  )
}
